import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import type { Deal, TeamMember } from "../lib/types";
import DealLink from "../components/DealLink";
import DealCallText from "../components/DealCallText";
import DealAgeChips from "../components/shared/DealAgeChips";
import { daysInStage } from "../lib/dealAge";
import {
  PURSUABLE_STAGES,
  STAGE_LABELS,
  ROLE_LABEL,
  EXEC_ROLES,
  resolvePlayReps,
  type PlayRep,
} from "../lib/playScope";
import {
  fetchTodayCrews,
  createMyCrew,
  addCrewMember,
  leaveCrew,
  notifyCrewAdded,
  activeMembers,
  type CrewJoined,
} from "../lib/playCrew";

// ---------------------------------------------------------------------------
// Run the Play — ASM/TL/RM pursuable-lead route planner.
// "I just walked out of an appointment, what now?" → pursuable leads (around me
// when GPS is on), grouped by stage, call/text on the spot, check the ones to
// pursue, drop them into a Google Maps route.
//
//   RM   → personal pursuables + today's DAY CREW (see below)
//   TL   → personal + his RMs
//   ASM  → personal + his TLs + his RMs
//   exec → everyone
//
// Day crews (Jul 12, 2026): RMs riding together can crew up for the day —
// mutual visibility of each other's pursuable-stage leads only, expires at ET
// midnight. Added reps get a heads-up text + can leave with one tap; ASM/TL
// see active crews in the coaching section. RLS enforcement lives in the
// deals_rm_crew_read policy + play_crewmate_ids() (migration
// run_the_play_day_crews) — the UI here is just the driver.
// ---------------------------------------------------------------------------

declare const mapboxgl: any;
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

const RADIUS_OPTIONS = [5, 10, 25, 50] as const;
const COLD_DAYS = 7; // amber "Aging"
const VERY_COLD_DAYS = 14; // red "Cold"

// Stage marker colors (map + chips)
const STAGE_COLOR: Record<string, string> = {
  initial_contact: "#f59e0b", // amber
  presentation_scheduled: "#14b8a6", // teal
  follow_up: "#6366f1", // indigo
};

type PlayDeal = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "property_street"
  | "property_city"
  | "property_state"
  | "property_zip"
  | "property_lat"
  | "property_lon"
  | "stage"
  | "days_in_stage"
  | "stage_entered_at"
  | "phase_entered_at"
  | "created_at"
  | "owner_id"
  | "split_rm_id"
  | "updated_at"
  | "homeowner_phone"
>;

type DecoratedDeal = PlayDeal & { distanceMi: number | null; mine: boolean };

const DEAL_COLS =
  "id,homeowner_name,property_street,property_city,property_state,property_zip,property_lat,property_lon,stage,days_in_stage,stage_entered_at,phase_entered_at,created_at,owner_id,split_rm_id,updated_at,homeowner_phone";

function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function addrOf(d: PlayDeal): string {
  return [d.property_street, d.property_city, d.property_state]
    .filter(Boolean)
    .join(", ");
}

// --- per-device preference persistence ---
interface Prefs {
  radiusMi?: number;
  showAllMine?: boolean;
  sortMode?: "near" | "cold";
  viewMode?: "list" | "map";
  selectedReps?: string[];
}
function loadPrefs(uid: string): Prefs {
  try {
    return JSON.parse(localStorage.getItem("rtp_prefs_" + uid) || "null") || {};
  } catch {
    return {};
  }
}
function savePrefs(uid: string, p: Prefs) {
  try {
    localStorage.setItem("rtp_prefs_" + uid, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

export default function RunThePlay() {
  const { member, isSuperUser } = useAuth();

  const [roster, setRoster] = useState<TeamMember[] | null>(null);
  const [deals, setDeals] = useState<PlayDeal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [selectedReps, setSelectedReps] = useState<Set<string>>(new Set());
  const [repPanelOpen, setRepPanelOpen] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set());

  // Day crews
  const [crews, setCrews] = useState<CrewJoined[]>([]);
  const [crewPanelOpen, setCrewPanelOpen] = useState(false);
  const [crewBusy, setCrewBusy] = useState<string | null>(null);
  const [crewErr, setCrewErr] = useState<string | null>(null);

  const [userLat, setUserLat] = useState<number | null>(null);
  const [userLon, setUserLon] = useState<number | null>(null);
  const [gpsStatus, setGpsStatus] = useState<
    "idle" | "requesting" | "active" | "denied" | "unavailable"
  >("idle");

  const [radiusMi, setRadiusMi] = useState(10);
  const [showAllMine, setShowAllMine] = useState(true);
  const [sortMode, setSortMode] = useState<"near" | "cold">("near");
  const [viewMode, setViewMode] = useState<"list" | "map">("list");

  const hydrated = useRef(false);

  const role = member?.role ?? "";
  const isExec = (!!member && isSuperUser) || EXEC_ROLES.has(role);

  // --- hydrate prefs once per user ---
  useEffect(() => {
    if (!member || hydrated.current) return;
    const p = loadPrefs(member.id);
    if (p.radiusMi) setRadiusMi(p.radiusMi);
    if (typeof p.showAllMine === "boolean") setShowAllMine(p.showAllMine);
    if (p.sortMode) setSortMode(p.sortMode);
    if (p.viewMode) setViewMode(p.viewMode);
    setSelectedReps(
      new Set(p.selectedReps && p.selectedReps.length ? p.selectedReps : [member.id]),
    );
    hydrated.current = true;
  }, [member]);

  // --- persist prefs ---
  useEffect(() => {
    if (!member || !hydrated.current) return;
    savePrefs(member.id, {
      radiusMi,
      showAllMine,
      sortMode,
      viewMode,
      selectedReps: [...selectedReps],
    });
  }, [member, radiusMi, showAllMine, sortMode, viewMode, selectedReps]);

  // --- GPS ---
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsStatus("unavailable");
      return;
    }
    setGpsStatus("requesting");
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLon(pos.coords.longitude);
        setGpsStatus("active");
      },
      (e) => setGpsStatus(e.code === e.PERMISSION_DENIED ? "denied" : "unavailable"),
      { enableHighAccuracy: true, maximumAge: 60_000, timeout: 10_000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // --- roster ---
  useEffect(() => {
    supabase
      .from("team_roster")
      .select("*")
      .eq("is_active", true)
      .in("role", ["restore_manager", "team_lead", "asm"])
      .then(({ data }) => setRoster((data as TeamMember[]) ?? []));
  }, []);

  // --- day crews: load today's crews + live-refresh on membership changes ---
  const reloadCrews = useCallback(() => {
    fetchTodayCrews()
      .then(setCrews)
      .catch((e) => console.warn("[RunThePlay] crew load failed:", e));
  }, []);

  useEffect(() => {
    reloadCrews();
  }, [reloadCrews]);

  useEffect(() => {
    const ch = supabase
      .channel("rtp-day-crews")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "play_crew_members" },
        () => reloadCrews(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "play_crews" },
        () => reloadCrews(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [reloadCrews]);

  // Crews I'm an active member of today (mine + ones I was added to).
  const myActiveCrews = useMemo(
    () =>
      member
        ? crews.filter((c) =>
            c.members.some((m) => m.member_id === member.id && !m.left_at),
          )
        : [],
    [crews, member],
  );

  // Everyone riding with me today (union across my crews, excluding me).
  const crewmateIds = useMemo(() => {
    if (!member) return [] as string[];
    const s = new Set<string>();
    for (const c of myActiveCrews)
      for (const m of activeMembers(c))
        if (m.member_id !== member.id) s.add(m.member_id);
    return [...s];
  }, [myActiveCrews, member]);

  const myOwnCrew = useMemo(
    () => (member ? (crews.find((c) => c.created_by === member.id) ?? null) : null),
    [crews, member],
  );
  const inOthersCrews = useMemo(
    () => (member ? myActiveCrews.filter((c) => c.created_by !== member.id) : []),
    [myActiveCrews, member],
  );

  // Hierarchy-scoped reps (the pre-crew behavior).
  const baseReps = useMemo<PlayRep[]>(() => {
    if (!member || roster === null) return [];
    return resolvePlayReps(
      { id: member.id, full_name: member.full_name, role: member.role },
      roster,
      isExec,
    );
  }, [member, roster, isExec]);

  // Reps in the play = hierarchy scope + today's crewmates.
  const repOptions = useMemo<PlayRep[]>(() => {
    if (!member || roster === null) return [];
    const out = [...baseReps];
    const seen = new Set(out.map((r) => r.id));
    for (const id of crewmateIds) {
      if (seen.has(id)) continue;
      const m = roster.find((r) => r.id === id);
      if (!m) continue;
      out.push({ id: m.id, name: m.full_name, role: m.role, you: false, crew: true });
      seen.add(id);
    }
    return out;
  }, [member, roster, baseReps, crewmateIds]);

  // RMs I could crew up with (active, not me, not already in my hierarchy scope).
  const crewCandidates = useMemo(() => {
    if (!member || roster === null) return [] as TeamMember[];
    const baseIds = new Set(baseReps.map((r) => r.id));
    return roster
      .filter(
        (m) =>
          m.role === "restore_manager" && m.id !== member.id && !baseIds.has(m.id),
      )
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [member, roster, baseReps]);

  const inMyCrew = useCallback(
    (id: string) =>
      !!myOwnCrew && myOwnCrew.members.some((m) => m.member_id === id && !m.left_at),
    [myOwnCrew],
  );

  const toggleCrewmate = useCallback(
    async (rm: TeamMember) => {
      if (!member) return;
      setCrewBusy(rm.id);
      setCrewErr(null);
      try {
        if (myOwnCrew && inMyCrew(rm.id)) {
          await leaveCrew(myOwnCrew.id, rm.id);
          setSelectedReps((prev) => {
            const next = new Set(prev);
            next.delete(rm.id);
            return next;
          });
        } else {
          const crewId = myOwnCrew?.id ?? (await createMyCrew(member.id));
          // Re-assert my own membership row (heals a rare partial-create so
          // visibility stays MUTUAL — the crew only works if the creator is
          // a member too).
          await addCrewMember(crewId, member.id, member.id);
          await addCrewMember(crewId, rm.id, member.id);
          setSelectedReps((prev) => new Set([...prev, rm.id]));
          void notifyCrewAdded(rm.id, {
            id: member.id,
            full_name: member.full_name,
          });
        }
      } catch (e) {
        setCrewErr(e instanceof Error ? e.message : String(e));
      } finally {
        // Refresh on success AND failure — a 409 from a stale crews list
        // self-heals once the fresh state lands.
        reloadCrews();
        setCrewBusy(null);
      }
    },
    [member, myOwnCrew, inMyCrew, reloadCrews],
  );

  const leaveOthersCrew = useCallback(
    async (crewId: string) => {
      if (!member) return;
      setCrewBusy(crewId);
      setCrewErr(null);
      try {
        await leaveCrew(crewId, member.id);
      } catch (e) {
        setCrewErr(e instanceof Error ? e.message : String(e));
      } finally {
        reloadCrews();
        setCrewBusy(null);
      }
    },
    [member, reloadCrews],
  );

  // Prune any stale rep ids (departed reps) from the persisted selection, but
  // always keep the viewer themselves selectable.
  useEffect(() => {
    if (!member || repOptions.length === 0) return;
    const valid = new Set(repOptions.map((r) => r.id));
    setSelectedReps((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      if (next.size === 0) next.add(member.id);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repOptions]);

  const ownerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    if (member) map[member.id] = member.full_name;
    (roster ?? []).forEach((m) => (map[m.id] = m.full_name));
    return map;
  }, [member, roster]);

  // --- load pursuable-stage deals for selected reps ---
  useEffect(() => {
    let cancelled = false;
    const ids = [...selectedReps];
    if (ids.length === 0) {
      setDeals([]);
      return;
    }
    setErr(null);
    const inList = `(${ids.join(",")})`;
    supabase
      .from("deals")
      .select(DEAL_COLS)
      .in("stage", PURSUABLE_STAGES as unknown as string[])
      .or(`owner_id.in.${inList},split_rm_id.in.${inList}`)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) setErr(error.message);
        else setDeals((data as PlayDeal[]) ?? []);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedReps]);

  const gpsActive = gpsStatus === "active" && userLat != null && userLon != null;

  // --- decorate + radius filter + group by stage + sort ---
  const grouped = useMemo(() => {
    const byStage: Record<string, DecoratedDeal[]> = {
      initial_contact: [],
      presentation_scheduled: [],
      follow_up: [],
    };
    if (!deals || !member) return byStage;

    for (const d of deals) {
      const mine = d.owner_id === member.id || d.split_rm_id === member.id;
      let distanceMi: number | null = null;
      if (gpsActive && d.property_lat != null && d.property_lon != null) {
        distanceMi = haversineMiles(
          userLat!,
          userLon!,
          Number(d.property_lat),
          Number(d.property_lon),
        );
      }
      if (gpsActive) {
        if (mine) {
          if (!showAllMine && (distanceMi == null || distanceMi > radiusMi)) continue;
        } else if (distanceMi == null || distanceMi > radiusMi) {
          continue;
        }
      }
      const bucket = byStage[d.stage as string];
      if (bucket) bucket.push({ ...d, distanceMi, mine });
    }

    for (const k of Object.keys(byStage)) {
      byStage[k].sort((a, b) => {
        if (sortMode === "cold") {
          const ca = daysInStage(a);
          const cb = daysInStage(b);
          if (ca !== cb) return cb - ca;
          return (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity);
        }
        // near
        const da = a.distanceMi ?? Infinity;
        const db = b.distanceMi ?? Infinity;
        if (da !== db) return da - db;
        const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return tb - ta;
      });
    }
    return byStage;
  }, [deals, member, gpsActive, userLat, userLon, radiusMi, showAllMine, sortMode]);

  const shownDeals = useMemo(
    () => [
      ...grouped.initial_contact,
      ...grouped.presentation_scheduled,
      ...grouped.follow_up,
    ],
    [grouped],
  );
  const totalShown = shownDeals.length;

  // Coaching lens: per-rep pursuable counts across ALL fetched deals (not
  // radius-limited) so a manager can see who's sitting on a stack.
  const byRep = useMemo(() => {
    if (!deals) return [];
    const acc: Record<string, { total: number; stages: Record<string, number> }> =
      {};
    for (const d of deals) {
      const owner = d.owner_id ?? "—";
      if (!acc[owner])
        acc[owner] = {
          total: 0,
          stages: { initial_contact: 0, presentation_scheduled: 0, follow_up: 0 },
        };
      acc[owner].total += 1;
      const s = d.stage as string;
      if (acc[owner].stages[s] != null) acc[owner].stages[s] += 1;
    }
    return Object.entries(acc)
      .map(([id, v]) => ({ id, name: ownerNameById[id] ?? "Unassigned", ...v }))
      .sort((a, b) => b.total - a.total);
  }, [deals, ownerNameById]);

  const showOwnerTag =
    isExec || role === "asm" || role === "team_lead" || crewmateIds.length > 0;
  const isManagerLens = isExec || role === "asm" || role === "team_lead";

  // Crews with ≥2 active members — what the manager lens shows.
  const activeCrewsToday = useMemo(
    () => crews.filter((c) => activeMembers(c).length >= 2),
    [crews],
  );

  // --- selection helpers ---
  const toggleRep = useCallback((id: string) => {
    setSelectedReps((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const selectAllReps = useCallback(
    () => setSelectedReps(new Set(repOptions.map((r) => r.id))),
    [repOptions],
  );
  const justMe = useCallback(() => {
    if (member) setSelectedReps(new Set([member.id]));
  }, [member]);

  const toggleDeal = useCallback((id: string) => {
    setSelectedDeals((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const shownRoutableIds = useMemo(
    () =>
      shownDeals
        .filter((d) => d.property_lat != null && d.property_lon != null)
        .map((d) => d.id),
    [shownDeals],
  );
  const selectAllShown = useCallback(
    () => setSelectedDeals(new Set(shownRoutableIds)),
    [shownRoutableIds],
  );

  // --- route (nearest-first ordering) ---
  const routeUrl = useMemo(() => {
    if (selectedDeals.size === 0 || !deals) return "";
    const chosen = deals.filter(
      (d) =>
        selectedDeals.has(d.id) && d.property_lat != null && d.property_lon != null,
    );
    if (userLat != null && userLon != null) {
      chosen.sort(
        (a, b) =>
          haversineMiles(userLat, userLon, Number(a.property_lat), Number(a.property_lon)) -
          haversineMiles(userLat, userLon, Number(b.property_lat), Number(b.property_lon)),
      );
    }
    const pts = chosen.map((d) => `${d.property_lat},${d.property_lon}`);
    if (pts.length === 0) return "";
    const startPts =
      userLat != null && userLon != null ? [`${userLat},${userLon}`, ...pts] : pts;
    if (startPts.length === 1)
      return `https://www.google.com/maps/dir/?api=1&destination=${startPts[0]}&travelmode=driving`;
    const origin = startPts[0];
    const dest = startPts[startPts.length - 1];
    const waypoints = startPts.slice(1, -1).join("/");
    return waypoints
      ? `https://www.google.com/maps/dir/${origin}/${waypoints}/${dest}`
      : `https://www.google.com/maps/dir/${origin}/${dest}`;
  }, [selectedDeals, deals, userLat, userLon]);

  if (!member) return null;

  const canPickReps = repOptions.length > 1;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-bardin-ink">Run the Play</h1>
          <p className="text-sm text-slate-500">
            Pursuable leads to convert into claims
            {gpsActive ? " — nearest to you first." : "."}
          </p>
        </div>
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700 underline">
          ← Home
        </Link>
      </div>

      {/* Crews I was added to — loud + one-tap out */}
      {inOthersCrews.length > 0 && (
        <div className="space-y-2 mb-4">
          {inOthersCrews.map((c) => (
            <div
              key={c.id}
              className="flex flex-wrap items-center justify-between gap-2 bg-teal-50 border border-teal-200 rounded-xl px-4 py-2.5"
            >
              <span className="text-sm text-teal-900">
                🤝 You're in{" "}
                <span className="font-semibold">
                  {ownerNameById[c.created_by] ?? "a teammate"}'s
                </span>{" "}
                crew today — you can see each other's pursuable leads until
                midnight.
              </span>
              <button
                onClick={() => leaveOthersCrew(c.id)}
                disabled={crewBusy === c.id}
                className="px-3 py-1 rounded bg-white border border-teal-300 text-teal-800 text-xs font-semibold hover:bg-teal-100 disabled:opacity-50"
              >
                {crewBusy === c.id ? "Leaving…" : "Leave crew"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-xl shadow p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span
              className={
                "w-2.5 h-2.5 rounded-full " +
                (gpsStatus === "active"
                  ? "bg-emerald-500 animate-pulse"
                  : gpsStatus === "requesting"
                    ? "bg-amber-400 animate-pulse"
                    : "bg-slate-300")
              }
            />
            <span className="text-xs text-slate-500">
              {gpsStatus === "active"
                ? "GPS active"
                : gpsStatus === "requesting"
                  ? "Locating…"
                  : gpsStatus === "denied"
                    ? "Location denied — showing all selected"
                    : "Location off — showing all selected"}
            </span>
          </div>

          {/* View toggle */}
          <div className="ml-auto inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
            <button
              onClick={() => setViewMode("list")}
              className={
                "px-3 py-1.5 " +
                (viewMode === "list"
                  ? "bg-bardin-ink text-white"
                  : "bg-white text-slate-500 hover:bg-slate-50")
              }
            >
              List
            </button>
            <button
              onClick={() => setViewMode("map")}
              className={
                "px-3 py-1.5 border-l border-slate-200 " +
                (viewMode === "map"
                  ? "bg-bardin-ink text-white"
                  : "bg-white text-slate-500 hover:bg-slate-50")
              }
            >
              Map
            </button>
          </div>
        </div>

        {/* Radius + sort row */}
        <div className="flex flex-wrap items-center gap-3">
          {gpsActive && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500 uppercase">Within</span>
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                {RADIUS_OPTIONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRadiusMi(r)}
                    className={
                      "px-3 py-1 rounded text-xs font-medium " +
                      (radiusMi === r
                        ? "bg-white text-bardin-ink shadow-sm"
                        : "text-slate-500 hover:text-slate-700")
                    }
                  >
                    {r} mi
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500 uppercase">Sort</span>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setSortMode("near")}
                className={
                  "px-3 py-1 rounded text-xs font-medium " +
                  (sortMode === "near"
                    ? "bg-white text-bardin-ink shadow-sm"
                    : "text-slate-500 hover:text-slate-700")
                }
              >
                Nearest
              </button>
              <button
                onClick={() => setSortMode("cold")}
                className={
                  "px-3 py-1 rounded text-xs font-medium " +
                  (sortMode === "cold"
                    ? "bg-white text-bardin-ink shadow-sm"
                    : "text-slate-500 hover:text-slate-700")
                }
              >
                Going cold
              </button>
            </div>
          </div>
        </div>

        {gpsActive && (
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={showAllMine}
              onChange={(e) => setShowAllMine(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-bardin-teal focus:ring-bardin-teal"
            />
            Always show all my own leads (ignore the radius for mine)
          </label>
        )}

        {/* Rep selector */}
        {canPickReps && (
          <div className="border-t border-slate-100 pt-3">
            <button
              onClick={() => setRepPanelOpen((o) => !o)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-sm font-medium text-bardin-ink">
                Reps in the play
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {selectedReps.size} selected
                </span>
              </span>
              <span className="text-slate-400 text-xs">
                {repPanelOpen ? "▲ hide" : "▼ choose"}
              </span>
            </button>

            {repPanelOpen && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <button
                    onClick={selectAllReps}
                    className="px-3 py-1 rounded bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200"
                  >
                    Select all
                  </button>
                  <button
                    onClick={justMe}
                    className="px-3 py-1 rounded bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200"
                  >
                    Just me
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {repOptions.map((r) => (
                    <label
                      key={r.id}
                      className={
                        "flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm " +
                        (selectedReps.has(r.id)
                          ? "border-bardin-teal bg-teal-50"
                          : "border-slate-200 hover:bg-slate-50")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selectedReps.has(r.id)}
                        onChange={() => toggleRep(r.id)}
                        className="w-4 h-4 rounded border-slate-300 text-bardin-teal focus:ring-bardin-teal"
                      />
                      <span className="font-medium text-bardin-ink">
                        {r.name}
                        {r.you && <span className="text-bardin-teal"> (You)</span>}
                      </span>
                      <span
                        className={
                          "ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded " +
                          (r.crew
                            ? "bg-teal-100 text-teal-700"
                            : "bg-slate-100 text-slate-500")
                        }
                      >
                        {r.crew ? "🤝 Crew" : (ROLE_LABEL[r.role] ?? r.role)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Day crew — RMs riding together today */}
        {crewCandidates.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <button
              onClick={() => setCrewPanelOpen((o) => !o)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-sm font-medium text-bardin-ink">
                🤝 Day crew
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {myOwnCrew && activeMembers(myOwnCrew).length > 1
                    ? `${activeMembers(myOwnCrew).length - 1} riding with you`
                    : "add the reps you're working with today"}
                </span>
              </span>
              <span className="text-slate-400 text-xs">
                {crewPanelOpen ? "▲ hide" : "▼ choose"}
              </span>
            </button>

            {crewPanelOpen && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-slate-500">
                  Crew up with the RMs you're knocking with today. They get a
                  heads-up text and can leave any time; you'll see each other's
                  pursuable leads until midnight tonight.
                </p>
                {crewErr && (
                  <div className="text-xs text-red-600">
                    Couldn't update crew — {crewErr}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {crewCandidates.map((rm) => {
                    const on = inMyCrew(rm.id);
                    const busy = crewBusy === rm.id;
                    return (
                      <button
                        key={rm.id}
                        onClick={() => toggleCrewmate(rm)}
                        disabled={busy}
                        className={
                          "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left disabled:opacity-50 " +
                          (on
                            ? "border-teal-400 bg-teal-50"
                            : "border-slate-200 hover:bg-slate-50")
                        }
                      >
                        <span className="font-medium text-bardin-ink flex-1 min-w-0 truncate">
                          {rm.full_name}
                        </span>
                        <span
                          className={
                            "text-[10px] font-semibold px-1.5 py-0.5 rounded " +
                            (on
                              ? "bg-teal-100 text-teal-700"
                              : "bg-slate-100 text-slate-500")
                          }
                        >
                          {busy ? "…" : on ? "✓ in your crew" : "+ add"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active crews today — manager lens */}
        {isManagerLens && activeCrewsToday.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <div className="text-sm font-medium text-bardin-ink mb-2">
              Active crews today
              <span className="ml-2 text-xs font-normal text-slate-500">
                who's running together
              </span>
            </div>
            <div className="space-y-1">
              {activeCrewsToday.map((c) => {
                const names = activeMembers(c)
                  .map((m) => ownerNameById[m.member_id] ?? "—")
                  .join(" + ");
                return (
                  <div
                    key={c.id}
                    className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-teal-50/60"
                  >
                    <span>🤝</span>
                    <span className="font-medium text-bardin-ink flex-1 min-w-0 truncate">
                      {names}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      started{" "}
                      {new Date(c.created_at).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Coaching lens — per-rep counts */}
        {showOwnerTag && byRep.length > 1 && (
          <div className="border-t border-slate-100 pt-3">
            <button
              onClick={() => setCoachOpen((o) => !o)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-sm font-medium text-bardin-ink">
                By rep
                <span className="ml-2 text-xs font-normal text-slate-500">
                  who's sitting on pursuables
                </span>
              </span>
              <span className="text-slate-400 text-xs">
                {coachOpen ? "▲ hide" : "▼ show"}
              </span>
            </button>
            {coachOpen && (
              <div className="mt-3 space-y-1">
                {byRep.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-slate-50"
                  >
                    <span className="font-medium text-bardin-ink flex-1 min-w-0 truncate">
                      {r.name}
                    </span>
                    <span className="text-xs text-slate-400">
                      {r.stages.initial_contact}/{r.stages.presentation_scheduled}/
                      {r.stages.follow_up}
                    </span>
                    <span className="text-sm font-semibold text-bardin-teal w-8 text-right">
                      {r.total}
                    </span>
                  </div>
                ))}
                <div className="text-[10px] text-slate-400 px-3">
                  counts = Initial / Scheduled / Follow-up · total
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Route bar */}
      <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-600">
          {selectedDeals.size > 0 ? (
            <>
              <span className="font-semibold text-bardin-teal">{selectedDeals.size}</span>{" "}
              stops selected
            </>
          ) : (
            `${totalShown} pursuable ${totalShown === 1 ? "lead" : "leads"} — check the ones to pursue`
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {shownRoutableIds.length > 0 && (
            <button
              onClick={selectAllShown}
              className="px-3 py-1.5 rounded bg-slate-100 text-slate-600 text-sm hover:bg-slate-200"
            >
              Select all shown
            </button>
          )}
          {selectedDeals.size > 0 && (
            <button
              onClick={() => setSelectedDeals(new Set())}
              className="px-3 py-1.5 rounded bg-slate-100 text-slate-600 text-sm hover:bg-slate-200"
            >
              Clear
            </button>
          )}
          {selectedDeals.size > 0 && routeUrl && (
            <a
              href={routeUrl}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 flex items-center gap-1.5"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" /></svg>
              Build Route ({selectedDeals.size})
            </a>
          )}
        </div>
      </div>

      {err && (
        <div className="bg-red-50 text-red-700 p-4 rounded mb-4">
          Couldn't load leads — {err}
        </div>
      )}
      {deals === null && !err && (
        <div className="text-slate-500 p-4">Loading pursuable leads…</div>
      )}

      {/* MAP VIEW */}
      {deals !== null && viewMode === "map" && (
        <PlayMap
          deals={shownDeals}
          userLat={userLat}
          userLon={userLon}
          selectedIds={selectedDeals}
          onToggle={toggleDeal}
        />
      )}

      {/* LIST VIEW */}
      {deals !== null && viewMode === "list" && (
        <div className="space-y-5">
          {PURSUABLE_STAGES.map((stage) => {
            const items = grouped[stage];
            return (
              <section key={stage}>
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                    {STAGE_LABELS[stage]}
                  </h2>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                    {items.length}
                  </span>
                </div>

                {items.length === 0 ? (
                  <div className="bg-white rounded-xl shadow px-4 py-3 text-sm text-slate-400">
                    {gpsActive ? `No leads within ${radiusMi} mi.` : "No leads."}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {items.map((d) => {
                      const hasCoords = d.property_lat != null && d.property_lon != null;
                      return (
                        <li
                          key={d.id}
                          className={
                            "bg-white rounded-xl shadow transition-all " +
                            (selectedDeals.has(d.id)
                              ? "ring-2 ring-bardin-teal ring-offset-1"
                              : "")
                          }
                        >
                          <div className="p-4 flex items-start gap-3">
                            <label className="mt-0.5 shrink-0 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedDeals.has(d.id)}
                                onChange={() => toggleDeal(d.id)}
                                disabled={!hasCoords}
                                className="w-5 h-5 rounded border-slate-300 text-bardin-teal focus:ring-bardin-teal cursor-pointer disabled:opacity-30"
                              />
                            </label>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-bardin-ink text-sm">
                                  <DealLink id={d.id}>
                                    {d.homeowner_name ?? "(no name)"}
                                  </DealLink>
                                </span>
                                {showOwnerTag && d.owner_id && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                                    {d.mine ? "You" : ownerNameById[d.owner_id] ?? "—"}
                                  </span>
                                )}
                                {d.distanceMi != null && (
                                  <span className="text-xs text-slate-400">
                                    {d.distanceMi < 1
                                      ? `${(d.distanceMi * 5280).toFixed(0)} ft`
                                      : `${d.distanceMi.toFixed(1)} mi`}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {addrOf(d) || "(no address)"}
                                {!hasCoords && (
                                  <span className="text-red-400 ml-1">· No map pin</span>
                                )}
                              </div>
                              <DealAgeChips
                                deal={d}
                                warnAt={COLD_DAYS}
                                dangerAt={VERY_COLD_DAYS}
                                className="mt-1"
                              />
                              <div className="mt-2">
                                <DealCallText
                                  dealId={d.id}
                                  homeownerName={d.homeowner_name}
                                  homeownerPhone={d.homeowner_phone}
                                  compact
                                />
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Map view — pins for the currently-shown leads, colored by stage. Tap a pin
// to add/remove it from the route. Blue dot = you.
// ---------------------------------------------------------------------------
function PlayMap({
  deals,
  userLat,
  userLon,
  selectedIds,
  onToggle,
}: {
  deals: DecoratedDeal[];
  userLat: number | null;
  userLon: number | null;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const userMarkerRef = useRef<any>(null);
  const [mapErr, setMapErr] = useState<string | null>(null);

  // init once
  useEffect(() => {
    if (!mapDiv.current || mapRef.current) return;
    if (typeof mapboxgl === "undefined") {
      setMapErr("Map library not loaded");
      return;
    }
    const withCoords = deals.filter(
      (d) => d.property_lat != null && d.property_lon != null,
    );
    const center: [number, number] =
      userLat != null && userLon != null
        ? [userLon, userLat]
        : withCoords.length
          ? [Number(withCoords[0].property_lon), Number(withCoords[0].property_lat)]
          : [-84.388, 33.749]; // Atlanta fallback
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapDiv.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center,
      zoom: 11,
      attributionControl: false,
      cooperativeGestures: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;
    return () => {
      try {
        map.remove();
      } catch {
        /* ignore */
      }
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // user dot
  useEffect(() => {
    const map = mapRef.current;
    if (!map || userLat == null || userLon == null) return;
    if (userMarkerRef.current) userMarkerRef.current.remove();
    const el = document.createElement("div");
    el.style.cssText =
      "width:16px;height:16px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 2px #2563eb;";
    userMarkerRef.current = new mapboxgl.Marker({ element: el })
      .setLngLat([userLon, userLat])
      .addTo(map);
  }, [userLat, userLon]);

  // deal markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const pts: [number, number][] = [];
    deals.forEach((d) => {
      if (d.property_lat == null || d.property_lon == null) return;
      const lng = Number(d.property_lon);
      const lat = Number(d.property_lat);
      const selected = selectedIds.has(d.id);
      const color = STAGE_COLOR[d.stage as string] ?? "#64748b";
      const el = document.createElement("div");
      el.style.cssText = `width:18px;height:18px;border-radius:50%;background:${color};cursor:pointer;border:${selected ? "3px solid #0f766e" : "2px solid #fff"};box-shadow:0 1px 4px rgba(0,0,0,.4);`;
      el.title = `${d.homeowner_name ?? "(no name)"} — tap to ${selected ? "remove from" : "add to"} route`;

      const popup = new mapboxgl.Popup({ offset: 14, closeButton: false }).setHTML(
        `<div style="font:13px system-ui;max-width:200px">
           <div style="font-weight:600;color:#1e293b">${(d.homeowner_name ?? "(no name)").replace(/</g, "&lt;")}</div>
           <div style="color:#64748b;font-size:12px;margin-top:2px">${addrOf(d).replace(/</g, "&lt;") || "(no address)"}</div>
           <div style="color:#94a3b8;font-size:11px;margin-top:2px">${STAGE_LABELS[d.stage as string] ?? d.stage}${d.distanceMi != null ? " · " + d.distanceMi.toFixed(1) + " mi" : ""}</div>
         </div>`,
      );

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map);
      el.addEventListener("click", () => onToggle(d.id));
      markersRef.current.push(marker);
      pts.push([lng, lat]);
    });

    // fit to pins (+ user) once when there's something to show
    if (pts.length) {
      const all = [...pts];
      if (userLat != null && userLon != null) all.push([userLon, userLat]);
      const lons = all.map((p) => p[0]);
      const lats = all.map((p) => p[1]);
      try {
        map.fitBounds(
          [
            [Math.min(...lons), Math.min(...lats)],
            [Math.max(...lons), Math.max(...lats)],
          ],
          { padding: 50, maxZoom: 14, duration: 0 },
        );
      } catch {
        /* ignore */
      }
    }
  }, [deals, selectedIds, userLat, userLon, onToggle]);

  if (mapErr)
    return (
      <div className="bg-white rounded-xl shadow p-8 text-center text-slate-500">
        {mapErr}
      </div>
    );

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div ref={mapDiv} className="w-full h-[60vh] min-h-[360px]" />
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-[11px] text-slate-500 border-t border-slate-100">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full inline-block" style={{ background: STAGE_COLOR.initial_contact }} /> Initial
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full inline-block" style={{ background: STAGE_COLOR.presentation_scheduled }} /> Scheduled
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full inline-block" style={{ background: STAGE_COLOR.follow_up }} /> Follow-up
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full inline-block bg-blue-600" /> You
        </span>
        <span className="ml-auto">Tap a pin to add/remove from route</span>
      </div>
    </div>
  );
}
